import { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Bell,
  Key,
  Save,
  Loader2,
  Send,
  Smartphone,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Info,
  Upload,
  FileJson,
} from 'lucide-react';

// iter335c — Push is centrally managed via the shared super-admin Firebase
// project (das-academy-rm). Tenants must NOT see/edit Firebase Setup Guide,
// VAPID key, or Service-Account upload — those are platform-level. Flip to
// true only if per-tenant BYO Firebase is ever introduced.
const SHOW_FIREBASE_CONFIG = false;

const PushNotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [vapidKey, setVapidKey] = useState('');
  const [savedVapidKey, setSavedVapidKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [deviceStats, setDeviceStats] = useState(null);
  const [saStatus, setSaStatus] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Send notification dialog
  const [sendDialog, setSendDialog] = useState(false);
  const [sendForm, setSendForm] = useState({ title: '', body: '', role: 'parent', url: '/' });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vapidRes, statusRes, saRes] = await Promise.all([
        api.get('/push/vapid-key'),
        api.get('/push/status'),
        api.get('/push/admin/service-account-status'),
      ]);
      const key = vapidRes.data.vapid_key || '';
      setVapidKey(key);
      setSavedVapidKey(key);
      setDeviceStats(statusRes.data);
      setSaStatus(saRes.data);
    } catch (error) {
      console.error('Error fetching push settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVapidKey = async () => {
    if (!vapidKey.trim()) {
      toast.error('Please enter a VAPID key');
      return;
    }
    setSaving(true);
    try {
      await api.post('/push/admin/vapid-key', { vapid_key: vapidKey.trim() });
      setSavedVapidKey(vapidKey.trim());
      toast.success('VAPID key saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save VAPID key');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadServiceAccount = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/push/admin/service-account', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSaStatus({ configured: true, project_id: res.data.project_id });
      toast.success(`Service account uploaded! Project: ${res.data.project_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload service account');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSendNotification = async () => {
    if (!sendForm.title.trim() || !sendForm.body.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/push/admin/send', {
        title: sendForm.title,
        body: sendForm.body,
        role: sendForm.role,
        url: sendForm.url || '/',
      });
      toast.success(`Notification sent to ${res.data.sent} device(s)`);
      setSendDialog(false);
      setSendForm({ title: '', body: '', role: 'parent', url: '/' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Push Notifications">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Push Notifications">
      <div className="space-y-6" data-testid="push-notification-settings">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bell className="h-5 w-5 lg:h-6 lg:w-6 text-blue-600" />
            Push Notification Settings
          </h1>
          <p className="text-sm text-slate-500 mt-1">Configure web push notifications for your academy</p>
        </div>

        {/* Status Card */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-blue-500" />
              Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">VAPID Key</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {savedVapidKey ? (
                    <><CheckCircle className="h-4 w-4 text-green-500" /><span className="text-sm font-medium text-green-700">Configured</span></>
                  ) : (
                    <><AlertCircle className="h-4 w-4 text-amber-500" /><span className="text-sm font-medium text-amber-700">Not set</span></>
                  )}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Service Account</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {saStatus?.configured ? (
                    <><CheckCircle className="h-4 w-4 text-green-500" /><span className="text-sm font-medium text-green-700">Uploaded</span></>
                  ) : (
                    <><AlertCircle className="h-4 w-4 text-red-500" /><span className="text-sm font-medium text-red-700">Missing</span></>
                  )}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Your Devices</p>
                <p className="text-lg font-bold text-slate-900 mt-1">{deviceStats?.active_devices || 0}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">Ready to Send</p>
                <div className="flex items-center gap-1.5 mt-1">
                  {savedVapidKey && saStatus?.configured ? (
                    <Badge className="bg-green-100 text-green-700">Yes</Badge>
                  ) : (
                    <Badge className="bg-amber-100 text-amber-700">Setup needed</Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {SHOW_FIREBASE_CONFIG && (<>
        {/* Setup Guide */}
        <Card className="border-2 border-blue-200 bg-blue-50/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Setup Guide
            </CardTitle>
            <CardDescription>Follow these steps to enable push notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">1</div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Get your VAPID Key</p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Go to{' '}
                    <a href="https://console.firebase.google.com/project/das-academy-rm/settings/cloudmessaging" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                      Firebase Console → Cloud Messaging <ExternalLink className="h-3 w-3" />
                    </a>
                    {' '}→ Under "Web Push certificates", click <strong>Generate key pair</strong> → Copy the key
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">2</div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Paste it below and save</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold">3</div>
                <div>
                  <p className="font-medium text-slate-900 text-sm">Upload Service Account JSON (for backend sending)</p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    In Firebase Console → Project Settings → <strong>Service Accounts</strong> → Click <strong>Generate new private key</strong> → Save the JSON file as <code className="bg-blue-100 px-1 py-0.5 rounded text-[11px]">firebase-service-account.json</code> in the backend folder
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VAPID Key Configuration */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-500" />
              Web Push Certificate (VAPID Key)
            </CardTitle>
            <CardDescription>This key enables browsers to subscribe to push notifications from your app</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <Label htmlFor="vapid-key" className="text-sm">VAPID Key</Label>
                <Input
                  id="vapid-key"
                  value={vapidKey}
                  onChange={(e) => setVapidKey(e.target.value)}
                  placeholder="Paste your VAPID key from Firebase Console..."
                  className="mt-1 font-mono text-xs"
                  data-testid="vapid-key-input"
                />
                {savedVapidKey && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Current key: {savedVapidKey.substring(0, 20)}...
                  </p>
                )}
              </div>
              <Button
                onClick={handleSaveVapidKey}
                disabled={saving || !vapidKey.trim() || vapidKey === savedVapidKey}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-vapid-key-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save VAPID Key
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Service Account Upload */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <FileJson className="h-5 w-5 text-purple-500" />
              Firebase Service Account
            </CardTitle>
            <CardDescription>Required for the backend to send push notifications to devices</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {saStatus?.configured ? (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="font-medium text-green-800 text-sm">Service Account Uploaded</p>
                      {saStatus.project_id && <p className="text-xs text-green-600">Project: {saStatus.project_id}</p>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-amber-500" />
                    <p className="text-sm text-amber-800">No service account uploaded. Push notifications cannot be delivered until this is configured.</p>
                  </div>
                </div>
              )}

              <div>
                <p className="text-xs text-slate-600 mb-2">
                  Download from{' '}
                  <a href="https://console.firebase.google.com/project/das-academy-rm/settings/serviceaccounts/adminsdk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
                    Firebase Console → Service Accounts <ExternalLink className="h-3 w-3" />
                  </a>
                  {' '}→ Click <strong>Generate new private key</strong>
                </p>
                <label
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
                    uploading ? 'border-slate-300 bg-slate-50' : 'border-purple-300 hover:border-purple-400 hover:bg-purple-50'
                  }`}
                  data-testid="upload-service-account-label"
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                  ) : (
                    <Upload className="h-4 w-4 text-purple-500" />
                  )}
                  <span className="text-sm font-medium text-purple-700">
                    {uploading ? 'Uploading...' : saStatus?.configured ? 'Replace Service Account' : 'Upload Service Account JSON'}
                  </span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleUploadServiceAccount}
                    disabled={uploading}
                    className="hidden"
                    data-testid="upload-service-account-input"
                  />
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        </>)}

        {/* Send Notification */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
              <Send className="h-5 w-5 text-green-500" />
              Send Push Notification
            </CardTitle>
            <CardDescription>Broadcast a push notification to users</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => setSendDialog(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="open-send-notification-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              Compose Notification
            </Button>
          </CardContent>
        </Card>

        {/* Send Notification Dialog */}
        <Dialog open={sendDialog} onOpenChange={setSendDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-green-500" />
                Send Push Notification
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Target Audience *</Label>
                <Select value={sendForm.role} onValueChange={(v) => setSendForm({ ...sendForm, role: v })}>
                  <SelectTrigger data-testid="send-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">All Parents</SelectItem>
                    <SelectItem value="coach">All Coaches</SelectItem>
                    <SelectItem value="staff">All Staff</SelectItem>
                    <SelectItem value="admin">All Admins</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input
                  value={sendForm.title}
                  onChange={(e) => setSendForm({ ...sendForm, title: e.target.value })}
                  placeholder="e.g., Practice Cancelled Tomorrow"
                  data-testid="send-title-input"
                />
              </div>
              <div>
                <Label>Message *</Label>
                <Textarea
                  value={sendForm.body}
                  onChange={(e) => setSendForm({ ...sendForm, body: e.target.value })}
                  placeholder="e.g., Due to weather conditions, practice is cancelled for tomorrow..."
                  rows={3}
                  data-testid="send-body-input"
                />
              </div>
              <div>
                <Label>Link URL (optional)</Label>
                <Input
                  value={sendForm.url}
                  onChange={(e) => setSendForm({ ...sendForm, url: e.target.value })}
                  placeholder="/"
                  data-testid="send-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">Where users go when they tap the notification</p>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setSendDialog(false)}>Cancel</Button>
              <Button
                onClick={handleSendNotification}
                disabled={sending || !sendForm.title.trim() || !sendForm.body.trim()}
                className="bg-green-600 hover:bg-green-700"
                data-testid="send-notification-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Send
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default PushNotificationSettings;
